/**
 * QuoteViewDetails.js
 * Card / dashboard-style detail view for a single Quote.
 * Route: /admin/quotations/:quoteId/details
 *
 * Separate from the PDF view (QuoteDetails.js → /admin/quotations/:quoteId/preview).
 */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  Grid,
  Paper,
  Stack,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "pages/components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/DashboardNavbar";
import EnhancedTableHead from "pages/components/common/EnhancedTableHead";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AdminQuotationApiService } from "api/adminQuotation.api";
import { QUOTATION_STATUS_LABELS, INVOICE_STATUS_LABELS } from "config/statusLabels";
import { toast } from "react-hot-toast";
import { FILE_URL } from "config/constants";
import { QUOTES_LIST_ROUTE } from "pages/Quotes/utils/routes";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const statusConfig = {
  0: { label: "Draft", sx: { bgcolor: "#f3f4f6", color: "#6b7280", borderRadius: 6 } },
  1: { label: "Sent", sx: { bgcolor: "#fef9c3", color: "#854d0e", borderRadius: 6 } },
  2: { label: "Accepted", sx: { bgcolor: "#dcfce7", color: "#15803d", borderRadius: 6 } },
  3: { label: "Paid", sx: { bgcolor: "#dcfce7", color: "#15803d", borderRadius: 6 } },
  4: { label: "Overdue", sx: { bgcolor: "#fee2e2", color: "#b91c1c", borderRadius: 6 } },
  5: { label: "Cancelled", sx: { bgcolor: "#f3f4f6", color: "#6b7280", borderRadius: 6 } },
};

const formatCurrency = (v) => `$${Number(v || 0).toFixed(2)}`;
const formatDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString();
};

// ─── Sub-component: info row ──────────────────────────────────────────────────

// A flat line carries its own term — a length or a fixed finish — snapshotted
// from the plan when it was quoted.
const subscriptionTermText = (item) => {
  if (item?.subscriptionTerm !== "flat") return "";
  if (item?.subscriptionTermMonths) {
    const m = item.subscriptionTermMonths;
    return m % 12 === 0
      ? ` · ${m / 12} ${m / 12 === 1 ? "year" : "years"}`
      : ` · ${m} ${m === 1 ? "month" : "months"}`;
  }
  if (item?.subscriptionEndDate)
    return ` · until ${String(item.subscriptionEndDate).slice(0, 10)}`;
  return "";
};

const InfoRow = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between" py={0.75} borderBottom="1px solid #f0f0f0">
    <SoftTypography variant="caption" color="text.secondary" sx={{ minWidth: 140 }}>
      {label}
    </SoftTypography>
    <SoftTypography variant="body2" fontWeight="medium" textAlign="right">
      {value || "-"}
    </SoftTypography>
  </Box>
);

InfoRow.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

// ─── Component ────────────────────────────────────────────────────────────────

const QuoteViewDetails = () => {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [paymentId, setPaymentId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadQuote = async () => {
    try {
      setLoading(true);
      const response = await AdminQuotationApiService.getQuotation(quoteId);
      setQuote(response?.data?.data ?? null);
    } catch (error) {
      console.error(error);
      setQuote(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (quoteId) {
      loadQuote();
    }
  }, [quoteId]);

  const handleConfirmPayment = async () => {
    if (!paymentId.trim()) {
      toast.error("Please enter a Payment ID");
      return;
    }

    try {
      setSubmitting(true);
      await AdminQuotationApiService.confirmPayment(quote.id, { paymentId });
      toast.success("Payment confirmed successfully");
      setConfirmModalOpen(false);
      setPaymentId("");
      loadQuote(); // Refresh data
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Failed to confirm payment");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox py={3}>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card sx={{ p: 3, mb: 3 }}>
                <Skeleton variant="text" width={220} height={42} animation="wave" />
                <Skeleton variant="text" width={300} height={24} animation="wave" />
              </Card>
              <Card sx={{ p: 3, mb: 3 }}>
                <Skeleton variant="text" width={120} height={30} animation="wave" />
                <Grid container spacing={2} mt={0.5}>
                  <Grid item xs={12} md={6}>
                    {["name", "email", "phone", "org"].map((rowKey) => (
                      <Skeleton
                        key={`admin-customer-skeleton-${rowKey}`}
                        variant="text"
                        height={28}
                        animation="wave"
                      />
                    ))}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {["line1", "line2", "city", "country"].map((rowKey) => (
                      <Skeleton
                        key={`admin-address-skeleton-${rowKey}`}
                        variant="text"
                        height={28}
                        animation="wave"
                      />
                    ))}
                  </Grid>
                </Grid>
              </Card>
              <Card sx={{ p: 3 }}>
                <Skeleton variant="text" width={170} height={30} animation="wave" />
                {["row1", "row2", "row3", "row4", "row5"].map((rowKey) => (
                  <Skeleton
                    key={`admin-items-skeleton-${rowKey}`}
                    variant="rectangular"
                    height={36}
                    sx={{ borderRadius: 1, mt: 1 }}
                    animation="wave"
                  />
                ))}
              </Card>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Card sx={{ p: 3, mb: 3 }}>
                <Skeleton variant="text" width={140} height={30} animation="wave" />
                {["m1", "m2", "m3", "m4", "m5", "m6", "m7"].map((rowKey) => (
                  <Skeleton
                    key={`admin-meta-skeleton-${rowKey}`}
                    variant="text"
                    height={28}
                    animation="wave"
                  />
                ))}
              </Card>
              <Card sx={{ p: 3 }}>
                <Skeleton variant="text" width={120} height={30} animation="wave" />
                <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1, mt: 2 }} />
                <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1, mt: 1 }} />
              </Card>
            </Grid>
          </Grid>
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!quote) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox py={3}>
          <Card sx={{ p: 4, maxWidth: 600, mx: "auto", textAlign: "center" }}>
            <SoftTypography variant="h5" mb={2}>
              Quote not found
            </SoftTypography>
            <SoftButton
              variant="outlined"
              color="info"
              onClick={() => navigate(QUOTES_LIST_ROUTE)}
            >
              Back to Quotations
            </SoftButton>
          </Card>
        </SoftBox>
      </DashboardLayout>
    );
  }

  const cfg = statusConfig[Number(quote.status)] || {
    label:
      QUOTATION_STATUS_LABELS.find((entry) => Number(entry.value) === Number(quote.status))
        ?.label || `Status ${quote.status}`,
    sx: { bgcolor: "#f3f4f6", color: "#6b7280", borderRadius: 6 },
  };
  const customerName =
    quote.customerName ||
    [quote.billingAddress?.firstName, quote.billingAddress?.lastName].filter(Boolean).join(" ") ||
    quote.billingAddress?.name ||
    "-";
  const products = Array.isArray(quote.items) ? quote.items : [];
  const subtotal = products.reduce(
    (sum, p) => sum + Number(p.price || 0) * Number(p.quantity || 1),
    0
  );
  const docTotal = Number(quote.amount || subtotal);
  const downPayment = Number(quote.downPayment || 0);
  const paidToDate = Number(quote.paidToDate || 0);
  const balanceDue = Math.max(docTotal - downPayment - paidToDate, 0);

  const handleViewPdf = () => {
    if (!quote?.invoice?.invoicePdf) return;
    window.open(`${FILE_URL}/${quote.invoice.invoicePdf}`, "_blank");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
        mt={2}
      >
        <Grid item direction={"column"}>
          <Link to={QUOTES_LIST_ROUTE}>
            <SoftTypography variant="h5" fontSize="medium" component="a">
              Go Back
            </SoftTypography>
          </Link>
        </Grid>
        <Grid>
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
            sx={{ flexWrap: "wrap" }}
          >
            {/* <Grid item>
              <SoftButton
                variant="gradient"
                color="info"
                size="small"
                onClick={() => navigate(`/admin/quotations/${quote.id}/preview`)}
              >
                Preview
              </SoftButton>
            </Grid> */}
            {Number(quote.status) === 0 && (
              <Grid item>
                <SoftButton
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={() => navigate(`/admin/quotations/create?quoteId=${quote.id}`)}
                >
                  Edit Quote
                </SoftButton>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Stack>

      <SoftBox py={2}>
        <Grid container spacing={3} justifyContent="center">
          {/* ── Left: main info ─────────────────────────────────────────── */}
          <Grid item xs={12} lg={8}>
            {/* Header card */}
            <Card sx={{ p: 3, mb: 3 }}>
              <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <SoftTypography variant="h4" fontWeight="bold">
                    Quote #{quote.id}
                  </SoftTypography>
                  <SoftTypography variant="body2" color="text.secondary" mt={0.5}>
                    Created: {formatDate(quote.createdAt)}
                    {quote.updatedAt !== quote.createdAt && (
                      <> &bull; Updated: {formatDate(quote.updatedAt)}</>
                    )}
                  </SoftTypography>
                </Box>
                <SoftBox
                  px={2}
                  py={0.2}
                  width={"fit-content"}
                  borderRadius={1}
                  fontSize="small"
                  fontWeight="bold"
                  textAlign="center"
                  sx={cfg.sx}
                >
                  {cfg.label}
                </SoftBox>
              </SoftBox>
            </Card>

            {/* Customer section */}
            <Card sx={{ p: 3, mb: 3 }}>
              <SoftTypography variant="h6" fontWeight="bold" mb={1.5}>
                Customer
              </SoftTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <InfoRow label="Name" value={customerName} />
                  <InfoRow label="Email" value={quote.customerEmail} />
                  <InfoRow label="Phone" value="-" />
                </Grid>
                <Grid item xs={12} md={6}>
                  {quote.billingAddress?.addressLineOne ? (
                    <>
                      <SoftTypography variant="caption" color="text.secondary">
                        Billing Address
                      </SoftTypography>
                      <SoftTypography variant="body2" mt={0.5}>
                        {quote.billingAddress.addressLineOne}
                        {quote.billingAddress.addressLineTwo && (
                          <>, {quote.billingAddress.addressLineTwo}</>
                        )}
                        <br />
                        {[
                          quote.billingAddress.city,
                          quote.billingAddress.state,
                          quote.billingAddress.zipCode,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                        <br />
                        {quote.billingAddress.country}
                      </SoftTypography>
                    </>
                  ) : (
                    <SoftTypography variant="body2" color="text.secondary">
                      No billing address on file
                    </SoftTypography>
                  )}
                </Grid>
              </Grid>
            </Card>

            {/* Products */}
            <Card sx={{ p: 3, mb: 3 }}>
              <SoftTypography variant="h6" fontWeight="bold" mb={1.5}>
                Products / Line Items
              </SoftTypography>
              <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
                <Table size="small">
                  <EnhancedTableHead
                    headCells={productHeadCells}
                    onRequestSort={() => {}}
                    orderBy={null}
                    order={null}
                  />
                  <TableBody>
                    {products.map((p, idx) => (
                      <TableRow key={p.id || idx} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>
                          <strong>{p.title || p.name || "-"}</strong>
                          {p.description && (
                            <Box fontSize="12px" color="text.secondary">
                              {p.description}
                            </Box>
                          )}
                          {p.lineKind === "subscription" && (
                            <Box fontSize="12px" color="text.secondary">
                              Subscription
                              {p.subscriptionTerm ? ` · billed ${p.subscriptionTerm}` : ""}
                              {subscriptionTermText(p)}
                              {p.subscriptionLocationName ? ` · ${p.subscriptionLocationName}` : ""}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="center">{p.quantity || 1}</TableCell>
                        <TableCell align="right">{formatCurrency(p.price)}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(Number(p.price || 0) * Number(p.quantity || 1))}
                        </TableCell>
                      </TableRow>
                    ))}
                    {products.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <SoftTypography variant="button" color="text">
                            No line items
                          </SoftTypography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Totals */}
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Box minWidth={260}>
                  <InfoRow label="Subtotal" value={formatCurrency(subtotal)} />
                  {quote.tax > 0 && <InfoRow label="Tax" value={formatCurrency(quote.tax)} />}
                  {downPayment > 0 && (
                    <InfoRow label="Down Payment" value={formatCurrency(downPayment)} />
                  )}
                  {paidToDate > 0 && (
                    <InfoRow label="Paid To Date" value={formatCurrency(paidToDate)} />
                  )}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    pt={1}
                    mt={0.5}
                    borderTop="2px solid"
                    borderColor="divider"
                  >
                    <SoftTypography variant="h6" fontWeight="bold">
                      Total
                    </SoftTypography>
                    <SoftTypography variant="h6" fontWeight="bold">
                      {formatCurrency(docTotal)}
                    </SoftTypography>
                  </Box>
                  {balanceDue !== docTotal && (
                    <Box display="flex" justifyContent="space-between" pt={0.5}>
                      <SoftTypography variant="body2" color="error">
                        Balance Due
                      </SoftTypography>
                      <SoftTypography variant="body2" color="error" fontWeight="bold">
                        {formatCurrency(balanceDue)}
                      </SoftTypography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Card>

            {/* Notes */}
            {(quote.note || quote.notes || quote.shippingRemarks) && (
              <Card sx={{ p: 3 }}>
                <SoftTypography variant="h6" fontWeight="bold" mb={1}>
                  Notes
                </SoftTypography>
                <SoftTypography variant="body2" color="text.secondary">
                  {quote.note || quote.notes || quote.shippingRemarks}
                </SoftTypography>
              </Card>
            )}
          </Grid>

          {/* ── Right: meta + actions ───────────────────────────────────── */}
          <Grid item xs={12} lg={4}>
            {/* Shipping & order meta */}
            <Card sx={{ p: 3, mb: 3 }}>
              <SoftTypography variant="h6" fontWeight="bold" mb={1.5}>
                Order Details
              </SoftTypography>
              <InfoRow label="SO Number" value={quote.soNumber} />
              <InfoRow label="Due Date" value={formatDate(quote.dueDate)} />
              <InfoRow label="Payment Terms" value={quote.paymentTerms} />
              <InfoRow label="Payment Mode" value={quote.paymentMode || quote.paymentMethod} />
              <InfoRow label="Tracking No." value={quote.trackingNumber} />
              <InfoRow label="Ship Via" value={quote.shipVia} />
              <InfoRow label="Shipping Acct." value={quote.shippingAccNumber} />
              {quote.acceptedAt && (
                <InfoRow label="Accepted At" value={formatDate(quote.acceptedAt)} />
              )}
            </Card>

            {/* Linked invoice */}
            {quote.invoiceId && (
              <Card sx={{ p: 3, mb: 3, border: "1px solid", borderColor: "success.main" }}>
                <SoftTypography variant="h6" fontWeight="bold" color="success" mb={1.5}>
                  Invoice Details {quote.invoiceSerialNumber ? `#${quote.invoiceSerialNumber}` : ""}
                </SoftTypography>
                <SoftTypography variant="body2" color="text.secondary" mb={2}>
                  An invoice is linked to this quotation.
                </SoftTypography>

                <InfoRow label="Invoice ID" value={quote.invoiceId} />
                <InfoRow
                  label="Invoice Status"
                  value={
                    INVOICE_STATUS_LABELS.find(
                      (l) => Number(l.value) === Number(quote.invoice?.status)
                    )?.label || "N/A"
                  }
                />
                <InfoRow label="Payment Mode" value={quote.invoice?.paymentMode} />
                <InfoRow label="PO Number" value={quote.invoice?.customerPoNumber} />
                <InfoRow label="Payment Terms" value={quote.invoice?.paymentTerms} />
                <InfoRow label="Amount" value={formatCurrency(quote.invoice?.amount)} />

                <SoftBox mt={2}>
                  {quote?.invoice?.invoicePdf && (
                    <SoftButton
                      variant="gradient"
                      color="success"
                      fullWidth
                      size="small"
                      onClick={handleViewPdf}
                      sx={{ mb: 1 }}
                    >
                      View Full Invoice
                    </SoftButton>
                  )}
                  {quote.invoice?.status === 2 &&
                    (quote.invoice?.paymentMode?.toLowerCase().includes("po") ||
                      quote.invoice?.paymentMode?.toLowerCase().includes("check")) && (
                      <SoftButton
                        variant="outlined"
                        color="success"
                        fullWidth
                        size="small"
                        onClick={() => setConfirmModalOpen(true)}
                      >
                        Confirm Payment
                      </SoftButton>
                    )}
                </SoftBox>
              </Card>
            )}
          </Grid>
        </Grid>
      </SoftBox>

      {/* Manual Payment Confirmation Modal */}
      <Dialog
        open={confirmModalOpen}
        onClose={() => !submitting && setConfirmModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Payment Manually</DialogTitle>
        <DialogContent>
          <SoftBox mt={1}>
            <SoftTypography variant="body2" color="text.secondary" mb={2}>
              Enter the Payment ID or Transaction Reference to mark this invoice and quotation as
              Paid.
            </SoftTypography>
            <TextField
              label="Payment ID / Reference"
              fullWidth
              value={paymentId}
              onChange={(e) => setPaymentId(e.target.value)}
              disabled={submitting}
              autoFocus
            />
          </SoftBox>
        </DialogContent>
        <DialogActions>
          <SoftButton
            color="secondary"
            onClick={() => setConfirmModalOpen(false)}
            disabled={submitting}
          >
            Cancel
          </SoftButton>
          <SoftButton
            variant="gradient"
            color="info"
            onClick={handleConfirmPayment}
            disabled={submitting}
          >
            {submitting ? "Confirming..." : "Confirm Payment"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

const productHeadCells = [
  { id: "no", label: "#", align: "left" },
  { id: "description", label: "Description", align: "left" },
  { id: "qty", label: "Qty", align: "center" },
  { id: "unitPrice", label: "Unit Price", align: "right" },
  { id: "lineTotal", label: "Line Total", align: "right" },
];

export default QuoteViewDetails;
